/**
 * Trace Context - Correlation ID Management
 *
 * Provides end-to-end request tracing through correlation IDs (trace IDs).
 * Used to link client-side errors to server-side operations.
 *
 * Usage:
 *   import { traceContext, withTrace } from '@/lib/traceContext';
 *
 *   // In API calls:
 *   const headers = { ...traceContext.getHeaders() };
 *
 *   // In error handlers:
 *   logger.error('Failed', { traceId: traceContext.getTraceId() });
 *
 *   // For scoped operations:
 *   withTrace(() => {
 *     // All operations here share the same traceId
 *   });
 */

// ============================================================================
// Types
// ============================================================================

export interface TraceContext {
  /** Unique trace ID for the current request/operation chain */
  traceId: string;
  /** Optional span ID for sub-operations */
  spanId?: string;
  /** Parent span ID for nested operations */
  parentSpanId?: string;
  /** When this trace started */
  startTime: number;
  /** Breadcrumbs of operations in this trace */
  breadcrumbs: TraceBreadcrumb[];
}

export interface TraceBreadcrumb {
  operation: string;
  timestamp: number;
  data?: Record<string, unknown>;
}

// ============================================================================
// Storage
// ============================================================================

// Use a simple variable for now - in Node.js we'd use AsyncLocalStorage
let currentContext: TraceContext | null = null;

// Session-level trace prefix for grouping related traces
const sessionId = generateId(8);

// ============================================================================
// ID Generation
// ============================================================================

/**
 * Generate a random ID of specified length
 */
function generateId(length: number = 16): string {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  for (let i = 0; i < length; i++) {
    result += chars[array[i] % chars.length];
  }
  return result;
}

/**
 * Generate a new trace ID
 * Format: {sessionId}-{timestamp}-{random}
 */
function generateTraceId(): string {
  const timestamp = Date.now().toString(36);
  const random = generateId(8);
  return `${sessionId}-${timestamp}-${random}`;
}

/**
 * Generate a new span ID
 */
function generateSpanId(): string {
  return generateId(8);
}

// ============================================================================
// Context Management
// ============================================================================

/**
 * Create a new trace context
 */
function createContext(parentContext?: TraceContext): TraceContext {
  return {
    traceId: parentContext?.traceId ?? generateTraceId(),
    spanId: generateSpanId(),
    parentSpanId: parentContext?.spanId,
    startTime: Date.now(),
    breadcrumbs: [],
  };
}

/**
 * Get the current trace context, creating one if necessary
 */
function getOrCreateContext(): TraceContext {
  if (!currentContext) {
    currentContext = createContext();
  }
  return currentContext;
}

// ============================================================================
// Public API
// ============================================================================

export const traceContext = {
  /**
   * Get the current trace ID
   */
  getTraceId(): string {
    return getOrCreateContext().traceId;
  },

  /**
   * Get the current span ID
   */
  getSpanId(): string | undefined {
    return getOrCreateContext().spanId;
  },

  /**
   * Get the session ID (groups all traces from this browser session)
   */
  getSessionId(): string {
    return sessionId;
  },

  /**
   * Get headers to include in API requests
   */
  getHeaders(): Record<string, string> {
    const ctx = getOrCreateContext();
    return {
      "X-Trace-Id": ctx.traceId,
      "X-Span-Id": ctx.spanId ?? "",
      "X-Session-Id": sessionId,
    };
  },

  /**
   * Add a breadcrumb to the current trace
   */
  addBreadcrumb(operation: string, data?: Record<string, unknown>): void {
    const ctx = getOrCreateContext();
    ctx.breadcrumbs.push({
      operation,
      timestamp: Date.now(),
      data,
    });

    // Keep only last 50 breadcrumbs to prevent memory issues
    if (ctx.breadcrumbs.length > 50) {
      ctx.breadcrumbs = ctx.breadcrumbs.slice(-50);
    }
  },

  /**
   * Get all breadcrumbs for the current trace
   */
  getBreadcrumbs(): TraceBreadcrumb[] {
    return getOrCreateContext().breadcrumbs;
  },

  /**
   * Get the full trace context
   */
  getContext(): TraceContext {
    return getOrCreateContext();
  },

  /**
   * Start a new trace (resets current context)
   */
  startTrace(): string {
    currentContext = createContext();
    return currentContext.traceId;
  },

  /**
   * Start a child span within the current trace
   */
  startSpan(): { spanId: string; parentSpanId?: string } {
    const ctx = getOrCreateContext();
    const parentSpanId = ctx.spanId;
    ctx.spanId = generateSpanId();
    ctx.parentSpanId = parentSpanId;
    return { spanId: ctx.spanId, parentSpanId };
  },

  /**
   * End the current trace
   */
  endTrace(): void {
    currentContext = null;
  },

  /**
   * Get trace duration in milliseconds
   */
  getDuration(): number {
    const ctx = getOrCreateContext();
    return Date.now() - ctx.startTime;
  },

  /**
   * Extract trace context from incoming headers (for server-side)
   */
  fromHeaders(headers: Headers | Record<string, string>): TraceContext {
    const get = (name: string): string | null => {
      if (headers instanceof Headers) {
        return headers.get(name);
      }
      return headers[name] ?? null;
    };

    const traceId = get("X-Trace-Id") ?? get("x-trace-id");
    const spanId = get("X-Span-Id") ?? get("x-span-id");

    if (traceId) {
      return {
        traceId,
        spanId: spanId ?? generateSpanId(),
        parentSpanId: spanId ?? undefined,
        startTime: Date.now(),
        breadcrumbs: [],
      };
    }

    return createContext();
  },
};

/**
 * Execute a function with a fresh trace context
 */
export function withTrace<T>(fn: () => T): T {
  const previousContext = currentContext;
  currentContext = createContext();

  try {
    return fn();
  } finally {
    currentContext = previousContext;
  }
}

/**
 * Execute an async function with a fresh trace context
 */
export async function withTraceAsync<T>(fn: () => Promise<T>): Promise<T> {
  const previousContext = currentContext;
  currentContext = createContext();

  try {
    return await fn();
  } finally {
    currentContext = previousContext;
  }
}

/**
 * Execute a function with a child span in the current trace
 */
export function withSpan<T>(operation: string, fn: () => T): T {
  const { spanId, parentSpanId } = traceContext.startSpan();
  traceContext.addBreadcrumb(operation, { spanId, parentSpanId });

  try {
    return fn();
  } finally {
    // Restore parent span
    const ctx = traceContext.getContext();
    ctx.spanId = parentSpanId;
    ctx.parentSpanId = undefined;
  }
}

/**
 * Execute an async function with a child span in the current trace
 */
export async function withSpanAsync<T>(
  operation: string,
  fn: () => Promise<T>,
): Promise<T> {
  const { spanId, parentSpanId } = traceContext.startSpan();
  traceContext.addBreadcrumb(operation, { spanId, parentSpanId });

  try {
    return await fn();
  } finally {
    // Restore parent span
    const ctx = traceContext.getContext();
    ctx.spanId = parentSpanId;
    ctx.parentSpanId = undefined;
  }
}
